import {
  InfiniteData,
  useInfiniteQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { boronClient } from "../../api";
import { createError } from "../../errors";
import { getNextPageParam } from "../../helpers/ReactQueryHelper";
import {
  ActiveOperator,
  Invitation,
} from "../../interfaces/SectionOperatorsInterface";
import { paths } from "../../lib/api/v1";

export const buildKey = (sectionId: string) =>
  ["/api/v1/sections/{section_id}/operators", sectionId] as const;

type Response =
  paths["/api/v1/sections/{section_id}/operators"]["get"]["responses"]["200"]["content"]["application/json"];

export const useFetchOperators = ({ sectionId }: { sectionId: string }) => {
  const query = useInfiniteQuery<Response>({
    queryKey: buildKey(sectionId),
    queryFn: async ({ pageParam = 1 }) => {
      const { data, response } = await boronClient.GET(
        "/api/v1/sections/{section_id}/operators",
        {
          params: {
            path: {
              section_id: sectionId,
            },
            query: {
              page: pageParam,
            },
          },
        },
      );
      if (response.ok && data) {
        return data;
      }
      throw await createError(response);
    },
    getNextPageParam: (lastPage) => {
      const activeOperators = lastPage?.activeOperators;
      if (!activeOperators) {
        return undefined;
      }
      return getNextPageParam(activeOperators);
    },
    initialPageParam: 1,
  });

  const activeOperators = query.data?.pages.flatMap(
    (page) => page?.activeOperators.data.map((operator) => operator) || [],
  );
  const invitingOperators = query.data?.pages[0]?.invitingOperators || [];

  return {
    ...query,
    activeOperators,
    invitingOperators,
  };
};

export const useOperatorsCacheUpdater = () => {
  const queryClient = useQueryClient();

  const addInvitingOperator = (sectionId: string, newOperator: Invitation) => {
    queryClient.setQueryData<InfiniteData<Response>>(
      buildKey(sectionId),
      (oldData) => {
        if (!oldData) {
          return oldData;
        }

        // 最初のページのinvitingOperatorsを更新
        const newPages = oldData.pages.map((page, index) => {
          if (index === 0) {
            return {
              ...page,
              invitingOperators: [newOperator, ...page.invitingOperators], // 招待日時の降順なので先頭に追加
            };
          }
          return page;
        });

        return {
          ...oldData,
          pages: newPages,
        };
      },
    );
  };

  const removeInvitingOperator = (sectionId: string, inviteId: string) => {
    queryClient.setQueryData<InfiniteData<Response>>(
      buildKey(sectionId),
      (oldData) => {
        if (!oldData) {
          return oldData;
        }

        // 最初のページのinvitingOperatorsから削除
        const newPages = oldData.pages.map((page, index) => {
          if (index === 0) {
            return {
              ...page,
              invitingOperators: page.invitingOperators.filter(
                (operator) => operator.id !== inviteId,
              ),
            };
          }
          return page;
        });

        return {
          ...oldData,
          pages: newPages,
        };
      },
    );
  };

  const removeActiveOperator = (sectionId: string, operatorId: string) => {
    queryClient.setQueryData<InfiniteData<Response>>(
      buildKey(sectionId),
      (oldData) => {
        if (!oldData) {
          return oldData;
        }

        // 全てのページのactiveOperatorsから削除
        const newPages = oldData.pages.map((page) => {
          return {
            ...page,
            activeOperators: {
              ...page.activeOperators,
              data: page.activeOperators.data.filter(
                (operator) => operator.id !== operatorId,
              ),
            },
          };
        });

        return {
          ...oldData,
          pages: newPages,
        };
      },
    );
  };

  const updateActiveOperator = (
    sectionId: string,
    activeOperator: ActiveOperator,
  ) => {
    queryClient.setQueryData<InfiniteData<Response>>(
      buildKey(sectionId),
      (oldData) => {
        if (!oldData) {
          return oldData;
        }

        // 全てのページのactiveOperatorsから該当するoperatorのroleを更新
        const newPages = oldData.pages.map((page) => {
          return {
            ...page,
            activeOperators: {
              ...page.activeOperators,
              data: page.activeOperators.data.map((operator) =>
                operator.id === activeOperator.id ? activeOperator : operator,
              ),
            },
          };
        });

        return {
          ...oldData,
          pages: newPages,
        };
      },
    );
  };

  return {
    addInvitingOperator,
    removeInvitingOperator,
    removeActiveOperator,
    updateActiveOperator,
  };
};
